export const CONFIG_KEYS = [
  'navigation',
  'homeSections',
  'theme',
  'terminology',
  'locale',
  'notifications',
  'density',
  'defaultView',
] as const

export type ConfigKey = (typeof CONFIG_KEYS)[number]
export type ConfigValue = string | number | boolean | readonly string[] | Readonly<Record<string, unknown>> | null
export type ConfigPatch = Readonly<Partial<Record<ConfigKey, ConfigValue>>>

export const PROTECTED_CONFIGURATION_KEYS = Object.freeze([
  'authorization',
  'tenantIsolation',
  'fieldVisibility',
  'audit',
  'stateTransition',
  'entitlement',
  'canonicalEntity',
  'consent',
] as const)

export type ConfigurationLayerId = 'pactlane_default' | 'workspace_preset' | 'role_preset' | 'user_override' | 'session_view'

export type ConfigurationLayer = Readonly<{
  id: ConfigurationLayerId
  schemaVersion: number
  values: Readonly<Record<string, unknown>>
}>

export type ConfigurationSchema = Readonly<{
  version: number
  allowedKeys: ReadonlySet<string>
  protectedKeys: ReadonlySet<string>
}>

export type ResolvedConfiguration = Readonly<{
  schemaVersion: number
  values: Readonly<Record<string, unknown>>
  provenance: Readonly<Record<string, ConfigurationLayerId | 'missing'>>
  missingKeys: readonly string[]
}>

export class ConfigurationValidationError extends Error {
  readonly code: 'schema_version_mismatch' | 'unknown_key' | 'protected_key'

  constructor(code: 'schema_version_mismatch' | 'unknown_key' | 'protected_key', detail: string) {
    super(`${code}:${detail}`)
    this.code = code
  }
}

export function validateConfigurationLayer(layer: ConfigurationLayer, schema: ConfigurationSchema): void {
  if (layer.schemaVersion !== schema.version) {
    throw new ConfigurationValidationError('schema_version_mismatch', `${layer.id}:${layer.schemaVersion}`)
  }
  for (const key of Object.keys(layer.values)) {
    if (schema.protectedKeys.has(key)) throw new ConfigurationValidationError('protected_key', `${layer.id}:${key}`)
    if (!schema.allowedKeys.has(key)) throw new ConfigurationValidationError('unknown_key', `${layer.id}:${key}`)
  }
}

export function resolveConfiguration(
  layers: readonly ConfigurationLayer[],
  schema: ConfigurationSchema,
): ResolvedConfiguration {
  const expectedOrder: readonly ConfigurationLayerId[] = [
    'pactlane_default', 'workspace_preset', 'role_preset', 'user_override', 'session_view',
  ]
  const byId = new Map(layers.map((layer) => [layer.id, layer] as const))
  const values: Record<string, unknown> = {}
  const provenance: Record<string, ConfigurationLayerId | 'missing'> = {}

  for (const id of expectedOrder) {
    const layer = byId.get(id)
    if (!layer) continue
    validateConfigurationLayer(layer, schema)
    for (const [key, value] of Object.entries(layer.values)) {
      values[key] = value
      provenance[key] = id
    }
  }

  const missingKeys: string[] = []
  for (const key of [...schema.allowedKeys].sort()) {
    if (!(key in values)) {
      provenance[key] = 'missing'
      missingKeys.push(key)
    }
  }

  return Object.freeze({
    schemaVersion: schema.version,
    values: Object.freeze({ ...values }),
    provenance: Object.freeze({ ...provenance }),
    missingKeys: Object.freeze(missingKeys),
  })
}

export type ConfigurationCacheEntry = Readonly<{
  tenantId: string
  epoch: number
  snapshot: ResolvedConfiguration
}>

export function isConfigurationCacheFresh(entry: ConfigurationCacheEntry, tenantId: string, currentEpoch: number): boolean {
  return entry.tenantId === tenantId && entry.epoch === currentEpoch
}
